import React, { useState } from 'react';
import { useAuth } from '../Context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, Sparkles } from 'lucide-react';

export default function Login({ onClose }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const { login } = useAuth();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        try {
            const response = await fetch('/api/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });

            if (response.ok) {
                const data = await response.json();
                login({ email, token: data.token, expiresAt: data.expiresAt });
                onClose();
            } else {
                const errorData = await response.json();
                setError(errorData.message || 'Failed to login');
            }
        } catch (err) {
            setError('An error occurred. Please try again.');
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex justify-center items-center z-50 p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="relative bg-gradient-to-br from-gray-900 via-purple-900/30 to-gray-900 p-8 rounded-2xl shadow-2xl w-full max-w-md border-2 border-fuchsia-500/40 shadow-[0_0_40px_rgba(255,0,255,0.3)]"
            >
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 rounded-full bg-black/60 hover:bg-red-500/20 border border-red-500/40 hover:border-red-500 transition-all group"
                >
                    <X size={20} className="text-red-400 group-hover:text-red-300" />
                </button>

                {/* Header */}
                <div className="text-center mb-8">
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring" }}
                        className="inline-block mb-4"
                    >
                        <Sparkles className="text-fuchsia-400" size={40} />
                    </motion.div>
                    <h2 className="text-3xl font-bold mb-2 bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-purple-600 bg-clip-text text-transparent">
                        Welcome Back
                    </h2>
                    <p className="text-gray-400 text-sm">Sign in to access upscaling features</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Email field */}
                    <div>
                        <label className="block text-gray-300 mb-2 text-sm font-semibold flex items-center gap-2" htmlFor="email">
                            <Mail size={16} className="text-fuchsia-400" />
                            Email
                        </label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full p-3 bg-black/40 border border-fuchsia-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-500/50 transition-all"
                            placeholder="your@email.com"
                            required
                        />
                    </div>

                    {/* Password field */}
                    <div>
                        <label className="block text-gray-300 mb-2 text-sm font-semibold flex items-center gap-2" htmlFor="password">
                            <Lock size={16} className="text-fuchsia-400" />
                            Password
                        </label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full p-3 bg-black/40 border border-fuchsia-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-500/50 transition-all"
                            placeholder="••••••••"
                            required
                        />
                    </div>

                    {/* Error message */}
                    <AnimatePresence>
                        {error && (
                            <motion.p
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="text-red-400 text-center text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-3"
                            >
                                {error}
                            </motion.p>
                        )}
                    </AnimatePresence>

                    {/* Submit button */}
                    <motion.button
                        type="submit"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white font-bold py-3 px-6 rounded-lg shadow-[0_0_20px_rgba(255,0,255,0.5)] hover:shadow-[0_0_30px_rgba(255,0,255,0.7)] transition-all"
                    >
                        Sign In
                    </motion.button>

                    {/* Cancel button */}
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full text-center text-gray-400 hover:text-white transition-colors text-sm"
                    >
                        Cancel
                    </button>
                </form>

                {/* Decorative elements */}
                <div className="absolute top-0 left-0 w-full h-full rounded-2xl overflow-hidden -z-10">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-fuchsia-500/10 rounded-full blur-3xl" />
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl" />
                </div>
            </motion.div>
        </motion.div>
    );
}
